import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { fmtHoraSeg, fmtFechaLarga, fechaISOhaceDias } from "@/lib/datetime";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import GuidedTour from "@/components/GuidedTour";
import TourHelpButton from "@/components/TourHelpButton";
import CheckinMap from "@/components/CheckinMap";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { useGuidedTour } from "@/hooks/useGuidedTour";
import { useAuth } from "@/contexts/AuthContext";
import { getBestGeoFix } from "@/lib/geo";
import PortalDocente from "@/pages/PortalDocente";
import { SignIn, SignOut, Coffee, Pause, MapPin, Clock, Camera, X, MoonStars } from "@phosphor-icons/react";
import { toast } from "sonner";

const TOUR_STEPS = [
  {
    target: "[data-tour='checkin-card']",
    title: "Registra tu jornada",
    content: "Aquí marcas tu entrada, tus descansos y tu salida. El botón disponible cambia según el momento del día en el que estés.",
  },
  {
    target: "[data-tour='resumen-hoy']",
    title: "Resumen de hoy",
    content: "Consulta tu hora de entrada, salida, horas efectivas trabajadas y si tuviste algún retardo en el día.",
  },
  {
    target: "[data-tour='ubicacion']",
    title: "Ubicación",
    content: "Al checar, tu ubicación se captura para validar que estés en tu centro de trabajo. Si estás fuera del rango permitido, tu registro queda marcado para revisión de tu supervisor.",
  },
  {
    target: "[data-tour='historial']",
    title: "Tu historial",
    content: "Aquí puedes revisar tus últimos 30 días de registros: horas ordinarias, horas extra y retardos.",
  },
];
const TOUR_STORAGE_KEY = "chkr_tour_portal_seen";

// tz = zona del centro del empleado (today.zona_horaria); si falta, la del navegador.
function fmtTime(iso, tz) {
  return fmtHoraSeg(iso, tz);
}

/** Reduce la foto a algo liviano (máx. 480px de lado, JPEG comprimido) antes de mandarla:
 * es evidencia de presencia, no una fotografía de archivo — no hace falta full-res. */
function downscaleFoto(file, maxDim = 480, quality = 0.6) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("No se pudo leer la foto"));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("No se pudo procesar la foto"));
      img.onload = () => {
        let { width, height } = img;
        if (width > height && width > maxDim) {
          height = Math.round((height * maxDim) / width);
          width = maxDim;
        } else if (height > maxDim) {
          width = Math.round((width * maxDim) / height);
          height = maxDim;
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

// Los docentes de una escuela checan por clase (varias marcas al día) y ven su carga:
// tienen su propio portal. El resto sigue con el de jornada.
export default function Portal() {
  const { user } = useAuth();
  if (user?.empresa_tipo === "escuela" && user?.tipo_personal === "docente") return <PortalDocente />;
  return <PortalJornada />;
}

function PortalJornada() {
  const { user } = useAuth();
  const [today, setToday] = useState(null);
  const [tz, setTz] = useState(undefined); // zona horaria del centro del empleado
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(new Date());
  const { active: tourActive, start: startTour, finish: finishTour } = useGuidedTour(TOUR_STORAGE_KEY);
  const [mapRow, setMapRow] = useState(null);
  const [foto, setFoto] = useState(null);
  const fotoInputRef = useRef(null);
  const [geoMsg, setGeoMsg] = useState(null);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const load = async () => {
    const [t, h] = await Promise.all([
      api.get("/jornadas/today"),
      api.get("/jornadas?fecha_desde=" + fechaISOhaceDias(30)),
    ]);
    setToday(t.data.registro);
    setTz(t.data.zona_horaria || undefined);
    setHistory(h.data);
  };
  useEffect(() => { load(); }, []);

  const onFotoSeleccionada = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setFoto(await downscaleFoto(file));
    } catch {
      toast.error("No se pudo procesar la foto, intenta de nuevo");
    }
  };

  const quitarFoto = () => {
    setFoto(null);
    if (fotoInputRef.current) fotoInputRef.current.value = "";
  };

  const checkin = async (tipo) => {
    setBusy(true);
    setGeoMsg("Obteniendo ubicación…");
    let geo = {};
    try {
      const pos = await getBestGeoFix({
        onUpdate: (acc) => setGeoMsg(`Obteniendo ubicación… (precisión: ${Math.round(acc)}m)`),
      });
      if (pos) {
        geo.geo_lat = pos.coords.latitude;
        geo.geo_lng = pos.coords.longitude;
      }
    } catch {}
    setGeoMsg(null);
    // Si el horario exige ubicación, el backend rechaza el checkin sin geo_lat/geo_lng y
    // devuelve un mensaje explicando por qué — no hace falta duplicar esa lógica aquí.
    const foto_base64 = (tipo === "entrada" || tipo === "salida") && foto ? foto : undefined;
    try {
      const { data } = await api.post("/jornadas/checkin", { tipo, ...geo, foto_base64 });
      // Solo avisar si ESTE evento envió ubicación — geo_dentro_geocerca puede venir
      // de un evento anterior del mismo día si esta vez no hubo permiso de geolocalización.
      if (geo.geo_lat != null && data.geo_dentro_geocerca === false) {
        toast.warning(`Registro ${tipo} guardado, pero tu ubicación está fuera del centro de trabajo (${Math.round(data.geo_distancia_metros)}m). Quedó marcado para revisión.`);
      } else {
        toast.success(`Registro ${tipo} guardado`);
      }
      quitarFoto();
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const enJornada = today?.hora_entrada && !today?.hora_salida;
  const ultimoDescanso = today?.descansos?.[today?.descansos?.length - 1];
  const enDescanso = ultimoDescanso && !ultimoDescanso.fin;

  return (
    <div className="p-6 md:p-10" data-testid="portal-page">
      <PageHeader
        kicker={`Hola, ${user?.name?.split(" ")[0] || ""}`}
        title="Mi Jornada"
        subtitle={now.toLocaleDateString("es-MX", { weekday: "long", year: "numeric", month: "long", day: "numeric", ...(tz ? { timeZone: tz } : {}) })}
        actions={<TourHelpButton onClick={startTour} />}
      />

      <GuidedTour steps={TOUR_STEPS} active={tourActive} onFinish={finishTour} />

      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-2 bg-brand text-white rounded-md p-8 relative overflow-hidden" data-testid="checkin-card" data-tour="checkin-card">
          <img src="/brand/monograma-blanco.png" alt="" aria-hidden="true" className="absolute -right-12 -bottom-16 w-72 opacity-[0.07] pointer-events-none select-none" />
          <div className="relative z-10">
            <div className="label-uppercase text-slate-400 mb-2">Hora actual</div>
            <div className="font-display text-6xl sm:text-7xl font-black tracking-tighter tabular-nums">
              {now.toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false, ...(tz ? { timeZone: tz } : {}) })}
            </div>
            <div className="mt-2 text-slate-300 text-sm">
              {today?.hora_entrada ? `Entrada registrada a las ${fmtTime(today.hora_entrada, tz)}` : "Aún no registras entrada"}
            </div>

            {today?.pertenece_a_ayer && (
              <div className="mt-2 inline-flex items-center gap-2 rounded-md border border-amber-400/30 bg-amber-500/15 px-3 py-1.5 text-xs text-amber-200" data-testid="portal-turno-de-ayer">
                <MoonStars size={14} weight="bold" />
                Estás terminando tu turno del {fmtFechaLarga(today.hora_entrada, tz)} — cruzó la medianoche.
              </div>
            )}

            {(!today?.hora_entrada || (enJornada && !enDescanso)) && (
              <div className="mt-5 flex items-center gap-3">
                {foto ? (
                  <div className="relative">
                    <img src={foto} alt="Foto de evidencia" className="w-14 h-14 rounded-md object-cover border border-white/20" data-testid="checkin-foto-preview" />
                    <button
                      type="button"
                      onClick={quitarFoto}
                      className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-brand border border-white/30 flex items-center justify-center text-white"
                      aria-label="Quitar foto"
                      data-testid="checkin-foto-quitar"
                    >
                      <X size={11} weight="bold" />
                    </button>
                  </div>
                ) : (
                  <label className="flex items-center gap-2 text-xs text-slate-300 border border-white/20 rounded-md px-3 py-2 cursor-pointer hover:bg-white/5 transition-colors">
                    <Camera size={16} />
                    Adjuntar foto (opcional)
                    <input
                      ref={fotoInputRef}
                      type="file"
                      accept="image/*"
                      capture="user"
                      className="hidden"
                      onChange={onFotoSeleccionada}
                      data-testid="checkin-foto-input"
                    />
                  </label>
                )}
              </div>
            )}

            {geoMsg && (
              <div className="mt-4 flex items-center gap-2 text-xs text-slate-300">
                <MapPin size={14} className="animate-pulse" />
                {geoMsg}
              </div>
            )}

            <div className="mt-8 grid grid-cols-2 gap-3">
              {!today?.hora_entrada && (
                <Button
                  onClick={() => checkin("entrada")}
                  disabled={busy}
                  className="col-span-2 h-16 bg-emerald-500 hover:bg-emerald-600 text-white font-display font-black text-xl"
                  data-testid="checkin-entrada-btn"
                >
                  <SignIn size={28} weight="bold" className="mr-3" />
                  REGISTRAR ENTRADA
                </Button>
              )}
              {enJornada && !enDescanso && (
                <>
                  <Button
                    onClick={() => checkin("inicio_descanso")}
                    disabled={busy}
                    variant="outline"
                    className="h-14 border-white/20 text-ink bg-white hover:bg-slate-100 font-bold"
                    data-testid="checkin-descanso-btn"
                  >
                    <Coffee size={20} weight="bold" className="mr-2" /> Iniciar descanso
                  </Button>
                  <Button
                    onClick={() => checkin("salida")}
                    disabled={busy}
                    className="h-14 bg-red-500 hover:bg-red-600 text-white font-display font-black text-lg"
                    data-testid="checkin-salida-btn"
                  >
                    <SignOut size={22} weight="bold" className="mr-2" /> SALIDA
                  </Button>
                </>
              )}
              {enDescanso && (
                <Button
                  onClick={() => checkin("fin_descanso")}
                  disabled={busy}
                  className="col-span-2 h-14 bg-amber-500 hover:bg-amber-600 text-white font-bold"
                  data-testid="checkin-fin-descanso-btn"
                >
                  <Pause size={20} weight="bold" className="mr-2" /> Terminar descanso
                </Button>
              )}
              {today?.hora_salida && (
                <div className="col-span-2 text-center py-6">
                  <div className="font-display text-2xl font-black">Jornada finalizada</div>
                  <div className="text-slate-300 mt-1">Salida: {fmtTime(today.hora_salida, tz)} · {today.horas_efectivas}h efectivas</div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-white border border-slate-200 rounded-md p-5" data-tour="resumen-hoy">
            <div className="label-uppercase mb-3"><Clock size={12} className="inline mr-1" /> Hoy</div>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between border-b border-slate-100 pb-2">
                <span className="text-slate-500">Entrada</span>
                <span className="font-mono font-bold">{fmtTime(today?.hora_entrada, tz)}</span>
              </div>
              <div className="flex justify-between border-b border-slate-100 pb-2">
                <span className="text-slate-500">Salida</span>
                <span className="font-mono font-bold">{fmtTime(today?.hora_salida, tz)}</span>
              </div>
              <div className="flex justify-between border-b border-slate-100 pb-2">
                <span className="text-slate-500">H. Efectivas</span>
                <span className="font-display font-black text-lg">{today?.horas_efectivas || 0}h</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Retardo</span>
                <span className={`font-bold ${(today?.minutos_retardo || 0) > 0 ? "text-amber-700" : ""}`}>{today?.minutos_retardo || 0}m</span>
              </div>
              {(today?.evidencia_entrada_url || today?.evidencia_salida_url || today?.evidencia_url) && (
                <div className="flex justify-between items-center pt-1">
                  <span className="text-slate-500">Evidencia</span>
                  <div className="flex gap-1" data-testid="portal-evidencia-hoy">
                    {today?.evidencia_entrada_url && (
                      <img src={today.evidencia_entrada_url} alt="Foto de evidencia de entrada" title="Entrada" className="w-9 h-9 rounded-md object-cover border border-slate-200" />
                    )}
                    {today?.evidencia_salida_url && (
                      <img src={today.evidencia_salida_url} alt="Foto de evidencia de salida" title="Salida" className="w-9 h-9 rounded-md object-cover border border-slate-200" />
                    )}
                    {!today?.evidencia_entrada_url && !today?.evidencia_salida_url && today?.evidencia_url && (
                      <img src={today.evidencia_url} alt="Foto de evidencia de hoy" className="w-9 h-9 rounded-md object-cover border border-slate-200" />
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-md p-5" data-tour="ubicacion">
            <div className="label-uppercase mb-2"><MapPin size={12} className="inline mr-1" /> Ubicación</div>
            {today?.geo_lat != null ? (
              <>
                <CheckinMap eventos={today.geo_eventos} lat={today.geo_lat} lng={today.geo_lng} distanciaMetros={today.geo_distancia_metros} dentroGeocerca={today.geo_dentro_geocerca} centro={tz ? { zona_horaria: tz } : null} height={180} />
                <p className="text-xs text-slate-600 mt-2">
                  {today.geo_dentro_geocerca === false
                    ? `Tu último check-in quedó a ${Math.round(today.geo_distancia_metros)} m del centro — marcado para revisión.`
                    : "Ubicación de cada evento que registraste hoy."}
                </p>
              </>
            ) : (
              <p className="text-xs text-slate-600">Al checar, capturamos tu ubicación para validar tu centro de trabajo (opcional según política).</p>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-tour="historial">
        <div className="px-5 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
          <h3 className="font-display font-bold">Mis últimos registros</h3>
          <span className="text-xs text-slate-500">{history.length} registros</span>
        </div>
        <div className="overflow-x-auto">
        <table className="data-table">
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Entrada</th>
              <th>Salida</th>
              <th>H. Ord</th>
              <th>H. Extra</th>
              <th>Retardo</th>
              <th>Estatus</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {history.length === 0 && <tr><td colSpan={8} className="text-center text-slate-500 py-8">Sin registros aún</td></tr>}
            {history.slice(0, 30).map((r) => (
              <tr key={r.id}>
                <td className="font-mono text-xs">{r.fecha}</td>
                <td className="font-mono">{fmtTime(r.hora_entrada, r.zona_horaria)}</td>
                <td className="font-mono">{fmtTime(r.hora_salida, r.zona_horaria)}</td>
                <td className="font-mono font-bold">{r.horas_ordinarias}</td>
                <td className="font-mono font-bold text-emerald-700">{r.horas_extra > 0 ? `+${r.horas_extra}` : "0"}</td>
                <td className={r.minutos_retardo > 0 ? "text-amber-700 font-bold" : "text-slate-500"}>{r.minutos_retardo}m</td>
                <td><span className="text-xs px-2 py-0.5 rounded bg-emerald-100 text-emerald-800">{r.estatus}</span></td>
                <td>
                  {r.geo_lat != null && (
                    <Button variant="ghost" size="sm" onClick={() => setMapRow(r)} data-testid={`portal-map-${r.id}`}>
                      <MapPin size={16} />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      <Dialog open={!!mapRow} onOpenChange={(o) => !o && setMapRow(null)}>
        <DialogContent data-testid="portal-map-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Ubicación del check-in · {mapRow?.fecha}</DialogTitle>
          </DialogHeader>
          {mapRow && (
            <CheckinMap eventos={mapRow.geo_eventos} lat={mapRow.geo_lat} lng={mapRow.geo_lng} distanciaMetros={mapRow.geo_distancia_metros} dentroGeocerca={mapRow.geo_dentro_geocerca} centro={(mapRow.zona_horaria || tz) ? { zona_horaria: mapRow.zona_horaria || tz } : null} height={340} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
